import React, { useState, useEffect, useCallback } from 'react';
import { colors, spacing, typography, borderRadius, shadows } from '../../styles/designTokens';
import { loadRevisions } from '../../utils/templateRevisions';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';

const TRIGGER_LABELS = {
  ai_improve: 'AI Improve',
  manual_save: 'Manual Save',
  autosave: 'Auto-save',
  restore: 'Restore',
  user: 'Manual Save',
};

function formatRelativeTime(date) {
  if (!date) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function countNodes(graph) {
  if (!graph?.nodes) return 0;
  return Object.keys(graph.nodes).length;
}

/**
 * Compute what changed between two graph snapshots.
 * olderGraph → newerGraph
 * Returns { added, removed, modified, renames } arrays.
 * Renames are detected when a removed and added node share identical script content.
 */
function computeGraphDiff(olderGraph, newerGraph) {
  const olderNodes = olderGraph?.nodes || {};
  const newerNodes = newerGraph?.nodes || {};
  const olderIds = new Set(Object.keys(olderNodes));
  const newerIds = new Set(Object.keys(newerNodes));

  const rawAdded = [...newerIds].filter(id => !olderIds.has(id));
  const rawRemoved = [...olderIds].filter(id => !newerIds.has(id));

  // Detect renames: a removed+added pair where script content matches exactly
  const renames = [];
  const matchedAdded = new Set();
  const matchedRemoved = new Set();

  const norm = (val) => (val == null ? '' : String(val).trim());
  rawRemoved.forEach(oldId => {
    const oldScript = norm(olderNodes[oldId]?.script);
    const match = rawAdded.find(
      newId => !matchedAdded.has(newId) && norm(newerNodes[newId]?.script) === oldScript
    );
    if (match) {
      renames.push({ from: oldId, to: match });
      matchedAdded.add(match);
      matchedRemoved.add(oldId);
    }
  });

  const added = rawAdded.filter(id => !matchedAdded.has(id));
  const removed = rawRemoved.filter(id => !matchedRemoved.has(id));
  const modified = [...newerIds].reduce((acc, id) => {
    if (!olderIds.has(id)) return acc;
    const o = olderNodes[id];
    const n = newerNodes[id];
    const changes = {};
    if (norm(o.script) !== norm(n.script)) changes.script = { from: o.script || '', to: n.script || '' };
    if (norm(o.coaching) !== norm(n.coaching)) changes.coaching = { from: o.coaching || '', to: n.coaching || '' };
    if (Object.keys(changes).length > 0) acc.push({ id, changes });
    return acc;
  }, []);

  return { added, removed, modified, renames };
}

/** Display label for a node ID — show the full ID as-is. */
function nodeLabel(id) {
  return id;
}

function DiffRow({ color, bg, textColor, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <span style={{ color: '#374151' }}>
        <strong style={{ color: textColor }}>{label}</strong>{' '}
        <span style={{ fontFamily: 'monospace', fontSize: '11px', background: bg, padding: '1px 4px', borderRadius: '3px' }}>
          {children}
        </span>
      </span>
    </div>
  );
}

/**
 * RevisionHistory
 *
 * Displays a list of saved revisions for a template and allows restoring any of them.
 */
const RevisionHistory = ({
  teamId,
  templateId,
  templateName,
  currentGraph,
  onRestore,
  onClose,
}) => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const fetchRevisions = useCallback(async () => {
    if (!teamId || !templateId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const results = await loadRevisions(teamId, templateId, 20);
    // Drop revisions that have no detectable graph changes vs the one before them
    const filtered = results.filter((rev, idx) => {
      const older = results[idx + 1];
      if (!older) return true; // always keep the oldest
      const diff = computeGraphDiff(older.graph, rev.graph);
      return (diff.added.length + diff.removed.length + diff.modified.length + diff.renames.length) > 0;
    });
    setRevisions(filtered);
    setLoading(false);
  }, [teamId, templateId]);

  useEffect(() => {
    fetchRevisions();
  }, [fetchRevisions]);

  const handleRestore = async (revision) => {
    setRestoring(revision.id);
    try {
      await onRestore(revision);
    } finally {
      setRestoring(null);
    }
  };

  const currentNodeCount = countNodes(currentGraph);

  const PanelHeader = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${spacing.sm} ${spacing.md}`,
      borderBottom: `1px solid ${colors.neutral[200]}`,
      flexShrink: 0,
    }}>
      <div>
        <h3 style={{ margin: 0, fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.neutral[900] }}>
          Revision History
        </h3>
        <p style={{ margin: '2px 0 0', fontSize: typography.small.size, color: colors.neutral[500] }}>
          {templateName}
        </p>
      </div>
      {onClose && <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>}
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader />
        <div style={{ padding: spacing.lg, textAlign: 'center', flex: 1 }}>
          <LoadingSpinner size="medium" message="Loading revision history..." />
        </div>
      </div>
    );
  }

  if (revisions.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader />
        <div style={{ padding: spacing.md, flex: 1 }}>
          <EmptyState
            icon="📜"
            title="No revision history yet"
            description="Snapshots are saved automatically before AI improvements are applied and when you save changes in the editor."
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${spacing.sm} ${spacing.md}`,
        borderBottom: `1px solid ${colors.neutral[200]}`,
        flexShrink: 0,
      }}>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: typography.h3.size,
            fontWeight: typography.h3.weight,
            color: colors.neutral[900],
          }}>
            Revision History
          </h3>
          <p style={{
            margin: `2px 0 0`,
            fontSize: typography.small.size,
            color: colors.neutral[500],
          }}>
            {templateName} &middot; {revisions.length} revision{revisions.length !== 1 ? 's' : ''}
          </p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        )}
      </div>

      {/* Current state indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        padding: `${spacing.xs} ${spacing.md}`,
        backgroundColor: colors.primary[50],
        borderBottom: `1px solid ${colors.primary[100]}`,
        flexShrink: 0,
      }}>
        <span style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: colors.primary.main,
          flexShrink: 0,
        }} />
        <span style={{
          fontSize: typography.small.size,
          fontWeight: 600,
          color: colors.primary.dark,
        }}>
          Current version
        </span>
        <span style={{
          fontSize: typography.small.size,
          color: colors.primary[600],
          marginLeft: 'auto',
        }}>
          {currentNodeCount} nodes
        </span>
      </div>

      {/* Revision list — fills remaining height */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {revisions.map((rev, idx) => {
          const isExpanded = previewId === rev.id;
          const revNodeCount = countNodes(rev.graph);
          const nodesDiff = currentNodeCount - revNodeCount;
          const triggerLabel = TRIGGER_LABELS[rev.triggeredBy] || rev.triggeredBy;

          // Diff: what changed IN this revision vs the one before it
          // revisions are newest-first, so "previous" = revisions[idx + 1]
          const olderGraph = revisions[idx + 1]?.graph || null;
          const diff = olderGraph ? computeGraphDiff(olderGraph, rev.graph) : null;
          const hasDiff = diff
            ? (diff.added.length + diff.removed.length + diff.modified.length + diff.renames.length) > 0
            : false;
          const diffSummary = diff
            ? [
                diff.modified.length > 0 && `${diff.modified.length} modified`,
                diff.renames.length > 0 && `${diff.renames.length} renamed`,
                diff.added.length > 0 && `${diff.added.length} added`,
                diff.removed.length > 0 && `${diff.removed.length} removed`,
              ].filter(Boolean).join(', ')
            : null;

          return (
            <div
              key={rev.id}
              style={{
                padding: `${spacing.sm} ${spacing.md}`,
                borderBottom: `1px solid ${colors.neutral[100]}`,
                backgroundColor: isExpanded ? colors.neutral[50] : 'transparent',
                transition: 'background-color 150ms ease',
              }}
            >
              {/* Summary row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  cursor: 'pointer',
                }}
                onClick={() => setPreviewId(isExpanded ? null : rev.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPreviewId(isExpanded ? null : rev.id);
                  }
                }}
                aria-expanded={isExpanded}
                aria-label={`Revision: ${rev.reason}`}
              >
                <span style={{ fontSize: '14px', color: colors.neutral[400], flexShrink: 0 }}>
                  {isExpanded ? '▾' : '▸'}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    flexWrap: 'wrap',
                  }}>
                    <span style={{
                      fontSize: typography.small.size,
                      fontWeight: 600,
                      color: colors.neutral[800],
                    }}>
                      {rev.reason}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      padding: '1px 6px',
                      borderRadius: borderRadius.sm,
                      backgroundColor: rev.triggeredBy === 'ai_improve'
                        ? colors.success[50]
                        : colors.neutral[100],
                      color: rev.triggeredBy === 'ai_improve'
                        ? colors.success.dark
                        : colors.neutral[600],
                      fontWeight: 500,
                    }}>
                      {triggerLabel}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: spacing.sm,
                    fontSize: '12px',
                    color: colors.neutral[500],
                    marginTop: '2px',
                    flexWrap: 'wrap',
                  }}>
                    <span>{formatRelativeTime(rev.savedAt)}</span>
                    <span>&middot;</span>
                    <span>{revNodeCount} nodes</span>
                    {hasDiff && (
                      <>
                        <span>&middot;</span>
                        <span style={{ color: colors.neutral[600] }}>{diffSummary}</span>
                      </>
                    )}
                    {rev.userEmail && (
                      <>
                        <span>&middot;</span>
                        <span>{rev.userEmail.split('@')[0]}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{
                  marginTop: spacing.sm,
                  padding: spacing.sm,
                  backgroundColor: 'white',
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.neutral[200]}`,
                }}>
                  <div style={{
                    fontSize: typography.small.size,
                    color: colors.neutral[600],
                    marginBottom: spacing.sm,
                    lineHeight: 1.6,
                  }}>
                    <div><strong>Saved:</strong> {rev.savedAt?.toLocaleString() || 'Unknown'}</div>
                    {rev.userEmail && <div><strong>By:</strong> {rev.userEmail.split('@')[0]}</div>}
                  </div>

                  {/* Diff vs previous revision */}
                  {diff && (
                    <div style={{
                      marginBottom: spacing.sm,
                      padding: spacing.sm,
                      backgroundColor: colors.neutral[50],
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.neutral[200]}`,
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: colors.neutral[500],
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '6px',
                      }}>
                        Changes since previous revision
                      </div>
                      {!hasDiff ? (
                        <div style={{ fontSize: '12px', color: colors.neutral[400] }}>No node changes detected</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {diff.modified.map(({ id, changes }) => (
                            <div key={id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <DiffRow color="#F59E0B" bg="#FEF3C7" textColor="#92400E" label="Modified">
                                {nodeLabel(id)}
                              </DiffRow>
                              {Object.entries(changes).map(([field, { from, to }]) => (
                                <div key={field} style={{ marginLeft: '14px', fontSize: '11px', color: colors.neutral[600] }}>
                                  <span style={{ fontWeight: 600, color: colors.neutral[500], textTransform: 'capitalize' }}>{field}:</span>
                                  {from && (
                                    <div style={{ marginTop: '2px', padding: '3px 6px', background: '#FEE2E2', borderRadius: '3px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      − {from.trim()}
                                    </div>
                                  )}
                                  <div style={{ marginTop: '2px', padding: '3px 6px', background: '#D1FAE5', borderRadius: '3px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    + {to.trim()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                          {diff.renames.map(({ from, to }) => (
                            <DiffRow key={from} color="#8B5CF6" bg="#EDE9FE" textColor="#5B21B6" label="Renamed">
                              {nodeLabel(from)} → {nodeLabel(to)}
                            </DiffRow>
                          ))}
                          {diff.added.map(id => (
                            <DiffRow key={id} color={colors.success.main} bg="#D1FAE5" textColor={colors.success.dark} label="Added">
                              {nodeLabel(id)}
                            </DiffRow>
                          ))}
                          {diff.removed.map(id => (
                            <DiffRow key={id} color={colors.error.main} bg="#FEE2E2" textColor={colors.error.dark} label="Removed">
                              {nodeLabel(id)}
                            </DiffRow>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {!diff && (
                    <div style={{
                      marginBottom: spacing.sm,
                      padding: spacing.sm,
                      backgroundColor: colors.neutral[50],
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.neutral[200]}`,
                      fontSize: '12px',
                      color: colors.neutral[400],
                    }}>
                      Oldest saved revision — no previous version to compare
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: spacing.xs }}>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={restoring === rev.id}
                      onClick={() => handleRestore(rev)}
                    >
                      Restore This Version
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewId(null)}
                    >
                      Cancel
                    </Button>
                  </div>

                  <p style={{
                    margin: `${spacing.xs} 0 0`,
                    fontSize: '11px',
                    color: colors.neutral[400],
                    lineHeight: 1.4,
                  }}>
                    Restoring will replace the current script with this version. A snapshot of the current version will be saved first.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RevisionHistory;
